import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2, TrendingUp, TrendingDown, DollarSign, CalendarDays, Building2, BarChart3, PieChart,
  Users, Star, ArrowUpRight, ArrowDownRight, Calendar, Download, Filter, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area, ComposedChart
} from 'recharts';

const COLORS = ['#FF5A5F', '#00A699', '#FFB400', '#767676', '#E31C1C', '#484848', '#6B46C1', '#2563EB'];

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
];

const EnhancedReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filter state
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Currency
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [baseCurrency, setBaseCurrency] = useState('USD');

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (period !== 'all') {
        params.period = period;
      }
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      
      const resp = await reportsAPI.overview(params);
      setData(resp.data);
      
      if (resp.data?.currency_symbol) {
        setCurrencySymbol(resp.data.currency_symbol);
        setBaseCurrency(resp.data.base_currency || 'USD');
      }
    } catch (err) {
      console.error('Reports error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = { format };
      if (period !== 'all') {
        params.period = period;
      }
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      if (format === 'csv') {
        const resp = await reportsAPI.export(params);
        const blob = new Blob([resp.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings_report_${period}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const resp = await reportsAPI.export({ ...params, format: 'json' });
        const blob = new Blob([JSON.stringify(resp.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings_report_${period}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return `${currencySymbol}${Number(value).toLocaleString()}`;
  };

  const formatPercent = (value) => `${value}%`;

  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return { value: 0, isPositive: true };
    const growth = ((current - previous) / previous) * 100;
    return { value: Math.abs(growth).toFixed(1), isPositive: growth >= 0 };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-airbnb-medium-gray">Failed to load reports</div>
    );
  }

  // Calculate some derived metrics
  const avgRevenuePerBooking = data.total_bookings > 0 ? data.total_revenue / data.total_bookings : 0;
  const avgRevenuePerProperty = data.total_properties > 0 ? data.total_revenue / data.total_properties : 0;
  const revenueGrowth = data.revenue_by_month.length >= 2
    ? calculateGrowth(
        data.revenue_by_month[data.revenue_by_month.length - 1]?.revenue || 0,
        data.revenue_by_month[data.revenue_by_month.length - 2]?.revenue || 0
      )
    : { value: 0, isPositive: true };
  const bookingGrowth = data.revenue_by_month.length >= 2
    ? calculateGrowth(
        data.revenue_by_month[data.revenue_by_month.length - 1]?.bookings || 0,
        data.revenue_by_month[data.revenue_by_month.length - 2]?.bookings || 0
      )
    : { value: 0, isPositive: true };

  // Prepare data for combined chart
  const combinedData = data.revenue_by_month.map(item => ({
    ...item,
    avgPrice: item.bookings > 0 ? Math.round(item.revenue / item.bookings) : 0
  }));

  return (
    <div data-testid="enhanced-reports-page" className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-airbnb-gray">Reports & Analytics</h1>
          <p className="text-airbnb-medium-gray mt-1">
            All amounts in {baseCurrency} ({currencySymbol})
            {data.date_range?.start && (
              <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">
                {data.date_range.start} to {data.date_range.end}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="rounded-xl"
            data-testid="export-csv-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('json')}
            disabled={exporting}
            className="rounded-xl"
            data-testid="export-json-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            JSON
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl"
            data-testid="refresh-reports-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-airbnb-medium-gray" />
            <span className="text-sm font-medium text-airbnb-gray">Period:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                data-testid={`period-${opt.value}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  period === opt.value
                    ? 'bg-airbnb-pink text-white'
                    : 'bg-gray-100 text-airbnb-gray hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {period === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36 h-8 text-sm"
                data-testid="start-date"
              />
              <span className="text-airbnb-medium-gray">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36 h-8 text-sm"
                data-testid="end-date"
              />
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Total Revenue</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="total-revenue">{formatCurrency(data.total_revenue)}</p>
          <div className="flex items-center gap-1 mt-2">
            {revenueGrowth.isPositive ? (
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
            ) : (
              <ArrowDownRight className="w-4 h-4 text-red-500" />
            )}
            <span className={`text-xs font-medium ${revenueGrowth.isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
              {revenueGrowth.value}% from last month
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">This Month</span>
            <div className="w-9 h-9 rounded-xl bg-airbnb-light-pink flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-airbnb-pink" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="month-revenue">{formatCurrency(data.month_revenue)}</p>
          <p className="text-xs text-airbnb-medium-gray mt-2">
            {data.upcoming_bookings} upcoming bookings
          </p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Occupancy Rate</span>
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="occupancy-rate">{data.occupancy_rate}%</p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(data.occupancy_rate, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Avg Stay Length</span>
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="avg-stay">{data.avg_stay_length} nights</p>
          <p className="text-xs text-airbnb-medium-gray mt-2">Across {data.total_bookings} bookings</p>
        </div>
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Total Bookings</span>
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="total-bookings">{data.total_bookings}</p>
          <div className="flex items-center gap-1 mt-2">
            {bookingGrowth.isPositive ? (
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
            ) : (
              <ArrowDownRight className="w-4 h-4 text-red-500" />
            )}
            <span className={`text-xs font-medium ${bookingGrowth.isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
              {bookingGrowth.value}% from last month
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Avg Revenue/Booking</span>
            <div className="w-9 h-9 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="avg-revenue-booking">{formatCurrency(avgRevenuePerBooking)}</p>
          <p className="text-xs text-airbnb-medium-gray mt-2">Per reservation</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Properties</span>
            <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-teal-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="total-properties">{data.total_properties}</p>
          <p className="text-xs text-airbnb-medium-gray mt-2">Active listings</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-airbnb-medium-gray">Revenue/Property</span>
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-airbnb-gray" data-testid="revenue-per-property">{formatCurrency(avgRevenuePerProperty)}</p>
          <p className="text-xs text-airbnb-medium-gray mt-2">Average per property</p>
        </div>
      </div>

      {/* Revenue & Booking Trends - Combined Chart */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-airbnb-pink" />
            <h3 className="font-bold text-airbnb-gray">Revenue & Booking Trends</h3>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-airbnb-pink" />
              <span className="text-airbnb-medium-gray">Revenue</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-airbnb-teal" />
              <span className="text-airbnb-medium-gray">Bookings</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-amber-400" />
              <span className="text-airbnb-medium-gray">Avg Price</span>
            </div>
          </div>
        </div>
        <div className="h-80" data-testid="combined-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={combinedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#767676' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#767676' }} tickFormatter={(v) => `${currencySymbol}${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#767676' }} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid #f0f0f0', fontSize: '12px' }}
                formatter={(value, name) => {
                  if (name === 'revenue' || name === 'avgPrice') return [`${currencySymbol}${value.toLocaleString()}`, name === 'revenue' ? 'Revenue' : 'Avg Price'];
                  return [value, 'Bookings'];
                }}
              />
              <Bar yAxisId="left" dataKey="revenue" fill="#FF5A5F" radius={[6, 6, 0, 0]} name="revenue" />
              <Line yAxisId="right" type="monotone" dataKey="bookings" stroke="#00A699" strokeWidth={2} dot={{ fill: '#00A699', r: 4 }} />
              <Line yAxisId="left" type="monotone" dataKey="avgPrice" stroke="#FFB400" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#FFB400', r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* By Channel */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-purple-500" />
            <h3 className="font-bold text-airbnb-gray">By Channel</h3>
          </div>
          <div className="h-52" data-testid="channel-chart">
            {data.bookings_by_channel.length === 0 ? (
              <p className="text-sm text-airbnb-medium-gray text-center pt-16">No data for this period</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={data.bookings_by_channel}
                    cx="50%" cy="50%"
                    outerRadius={80} innerRadius={40}
                    dataKey="count" nameKey="channel"
                    label={({ channel, count }) => `${channel} (${count})`}
                    labelLine={false}
                  >
                    {data.bookings_by_channel.map((entry, i) => <Cell key={`channel-${entry.channel || i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px' }} />
                </RePieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* By Status */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-airbnb-gray">By Status</h3>
          </div>
          <div className="h-52" data-testid="status-chart">
            {data.bookings_by_status.length === 0 ? (
              <p className="text-sm text-airbnb-medium-gray text-center pt-16">No data for this period</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={data.bookings_by_status}
                    cx="50%" cy="50%"
                    outerRadius={80} innerRadius={40}
                    dataKey="count" nameKey="status"
                    label={({ status, count }) => `${status} (${count})`}
                    labelLine={false}
                  >
                    {data.bookings_by_status.map((entry, i) => <Cell key={`status-${entry.status || i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px' }} />
                </RePieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Revenue by Property */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-blue-500" />
            <h3 className="font-bold text-airbnb-gray">Top Properties</h3>
          </div>
          <div className="space-y-3" data-testid="property-revenue-list">
            {data.revenue_by_property.length === 0 ? (
              <p className="text-sm text-airbnb-medium-gray text-center">No data for this period</p>
            ) : (
              data.revenue_by_property.map((p, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 mr-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-airbnb-gray truncate">{p.property}</span>
                  </div>
                  <span className="text-sm font-semibold text-airbnb-pink">{formatCurrency(p.revenue)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Currency Conversion Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm text-amber-800">
          <strong>Currency Conversion:</strong> Revenue figures are automatically converted to your base currency ({baseCurrency}) using approximate exchange rates. 
          For properties priced in different currencies, amounts are converted for unified reporting.
        </p>
      </div>
    </div>
  );
};

export default EnhancedReportsPage;
